import { apiClient } from './api'
import { resolveEventId } from './eventService'

/**
 * Attendee-facing registration + option-selection client.
 *
 * All calls are scoped to the active event (resolved by slug via eventService)
 * and require the attendee session cookie (apiClient sends it with
 * `withCredentials: true`). Business rules are enforced server-side; on a rule
 * violation the API returns 4xx with a human-readable `detail` message.
 */

export interface RegistrationField {
  id: string
  ordinal: number
  fieldType: string
  label: string
  required: boolean
  options?: string | null
  helperText?: string | null
}

export interface RegistrationView {
  registered: boolean
  status?: string | null
  values: Record<string, unknown>
}

export interface OptionView {
  id: string
  optionGroupId: string
  title: string
  description?: string | null
  location?: string | null
  startsAt?: string | null
  endsAt?: string | null
  capacity?: number | null
  remaining?: number | null
  isFull: boolean
  eligible: boolean
  selected: boolean
}

export interface OptionGroupView {
  id: string
  title: string
  description?: string | null
  minSelect: number
  maxSelect: number
  requiresRegistration: boolean
  selectionDeadline?: string | null
  locked: boolean
  selectedOptionId?: string | null
  options: OptionView[]
}

// --- Public self-service registration ------------------------------------

export interface PublicRegistrationInfo {
  id: string
  name: string
  slug: string
  startsAt?: string | null
  endsAt?: string | null
  location?: string | null
  tagline?: string | null
  heroImage?: string | null
  logoUrl?: string | null
  primaryColor?: string | null
  accentColor?: string | null
  closesAt?: string | null
  formToken: string
  fields: RegistrationField[]
  captchaEnabled: boolean
  // True when this event requires an invitation code (the code value itself is
  // never sent to the client).
  registrationCodeRequired: boolean
}

export interface PublicRegisterPayload {
  email: string
  fullName?: string
  company?: string
  values: Record<string, unknown>
  formToken?: string
  captchaToken?: string
  website?: string // honeypot — always sent empty
  registrationCode?: string // required when registrationCodeRequired is true
}

export const publicRegistrationService = {
  getInfo: (slug: string): Promise<PublicRegistrationInfo> =>
    apiClient
      .get<PublicRegistrationInfo>(
        `/public/events/${encodeURIComponent(slug)}/registration`,
      )
      .then((r) => r.data),

  register: (slug: string, payload: PublicRegisterPayload): Promise<void> =>
    apiClient
      .post(`/public/events/${encodeURIComponent(slug)}/register`, payload)
      .then(() => undefined),

  confirm: (token: string): Promise<void> =>
    apiClient
      .get(`/public/registration/confirm`, { params: { token } })
      .then(() => undefined),
}

export const registrationService = {
  getFields: async (): Promise<RegistrationField[]> => {
    const id = await resolveEventId()
    return apiClient
      .get<RegistrationField[]>(`/events/${id}/registration/fields`)
      .then((r) => r.data)
  },

  getMyRegistration: async (): Promise<RegistrationView> => {
    const id = await resolveEventId()
    return apiClient
      .get<RegistrationView>(`/events/${id}/registration/me`)
      .then((r) => r.data)
  },

  register: async (values: Record<string, unknown>): Promise<RegistrationView> => {
    const id = await resolveEventId()
    return apiClient
      .post<RegistrationView>(`/events/${id}/register`, { values })
      .then((r) => r.data)
  },

  cancelRegistration: async (): Promise<RegistrationView> => {
    const id = await resolveEventId()
    return apiClient
      .post<RegistrationView>(`/events/${id}/register/cancel`)
      .then((r) => r.data)
  },

  getOptionGroups: async (): Promise<OptionGroupView[]> => {
    const id = await resolveEventId()
    return apiClient
      .get<OptionGroupView[]>(`/events/${id}/option-groups`)
      .then((r) => r.data)
  },

  selectOption: async (groupId: string, optionId: string): Promise<void> => {
    const id = await resolveEventId()
    await apiClient.post(`/events/${id}/option-groups/${groupId}/select`, {
      optionId,
    })
  },

  unselectOption: async (groupId: string): Promise<void> => {
    const id = await resolveEventId()
    await apiClient.post(`/events/${id}/option-groups/${groupId}/unselect`)
  },
}
